import React, { useEffect, useMemo } from "react";
import { Drawer, Stack, Text, Center, Loader, Divider } from "@mantine/core";
import { IconCalendarOff } from "@tabler/icons-react";
import { ScheduledRow } from "./ScheduledRow";
import { AppointmentScheduledRow } from "./AppointmentScheduledRow";
import { useAppointmentScheduledMessageActions } from "../../hooks/useAppointmentScheduledMessageActions";
import { useAppointmentScheduledMessagePreviews } from "../../hooks/useAppointmentScheduledMessagePreviews";
import { TERMINAL_STATUSES } from "../../utils/appointmentScheduledMessageDisplay";
import type { UseScheduledMessagesResult } from "./useScheduledMessages";

interface ScheduledPanelProps {
  opened: boolean;
  onClose: () => void;
  timezone: string;
  /** Shared scheduled-messages state (chat sends + automated appointment msgs). */
  scheduled: UseScheduledMessagesResult;
}

/**
 * Scheduled-sends drawer: the conversation's UPCOMING chat scheduled sends AND
 * the client's automated appointment messages (confirmations, reminders,
 * forms/waivers, post-appointment) in one place — so staff see every scheduled
 * Twilio message going to the client, not just the per-appointment view in the
 * Transaction Modal. Chat sends are editable; automated messages offer the same
 * send-now / cancel / retry as the modal via the shared actions hook. New chat
 * sends are composed from the composer's Schedule-send modal. Times render in
 * the salon's timezone.
 */
export const ScheduledPanel: React.FC<ScheduledPanelProps> = ({
  opened,
  onClose,
  timezone,
  scheduled,
}) => {
  const { chatItems, appointmentItems, loading, refresh } = scheduled;
  const apptActions = useAppointmentScheduledMessageActions(refresh);

  // Surface what is still going out (SCHEDULED / QUEUED / SENDING / CHANGED) plus
  // FAILED sends, which staff can retry from the row. Hide the done-and-not-
  // actionable terminal rows (SENT / SKIPPED / CANCELLED). ("Your scheduled
  // sends" is already pending-only.)
  const visibleAppointmentItems = useMemo(
    () =>
      appointmentItems.filter(
        (m) => !TERMINAL_STATUSES.has(m.status) || m.status === "FAILED",
      ),
    [appointmentItems],
  );
  // Rendered SMS bodies for the visible rows. Appointment messages carry no body
  // of their own — the text is rendered on demand by the compliance preview
  // endpoint, one call per appointment (grouped inside the hook).
  const previews = useAppointmentScheduledMessagePreviews(
    visibleAppointmentItems,
  );

  // Refresh whenever the drawer is opened so staff see the current state.
  useEffect(() => {
    if (opened) refresh();
  }, [opened, refresh]);

  const isEmpty =
    !loading && chatItems.length === 0 && visibleAppointmentItems.length === 0;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      title="Scheduled messages"
      size="md"
    >
      <Stack gap="md">
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : isEmpty ? (
          <Center py="lg">
            <Stack align="center" gap={4}>
              <IconCalendarOff size={28} stroke={1.5} opacity={0.5} />
              <Text c="dimmed" size="sm">
                No scheduled messages.
              </Text>
            </Stack>
          </Center>
        ) : (
          <>
            {chatItems.length > 0 && (
              <Stack gap="sm">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  Your scheduled sends
                </Text>
                {chatItems.map((item) => (
                  <ScheduledRow
                    key={item.scheduled_id}
                    item={item}
                    timezone={timezone}
                    onChanged={refresh}
                  />
                ))}
              </Stack>
            )}
            {chatItems.length > 0 && visibleAppointmentItems.length > 0 && (
              <Divider />
            )}
            {visibleAppointmentItems.length > 0 && (
              <Stack gap="sm">
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                  Automated messages
                </Text>
                {visibleAppointmentItems.map((item) => (
                  <AppointmentScheduledRow
                    key={item.uuid}
                    item={item}
                    timezone={timezone}
                    actions={apptActions}
                    preview={previews[item.uuid]}
                  />
                ))}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Drawer>
  );
};

export default ScheduledPanel;
